import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  areStringsEqualIgnoreCase,
  Client,
  compareStringsIgnoreCase,
  emitValueIfValid,
  getControlValueChanges,
  getLoadedResults,
  ignoreEmpty,
  ignoreNullish,
  ignoreTruthy,
  isEmpty,
  isLoaded,
  Loadable,
  Validate,
} from '@aiwyn/shared';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, defer, merge, Observable, of, Subject } from 'rxjs';
import { AddOnInputType, AddOnOption } from '../../../../model';
import { PayWithoutInvoicePayable } from '../../models';
import { distinctUntilChanged, filter, map, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthAdaptorService } from '@aiwyn/auth';
import { BaseJob, Currency } from '@aiwyn/api';
import { CommonSettingsState, GetClientCurrencies } from '@aiwyn/common/settings';
import { Store } from '@ngxs/store';

const NON_NUMERIC_VALUES: (keyof PayWithoutInvoicePayable)[] = ['notes'];
const DEFAULT_PAYMENT_TYPE: AddOnOption = {
  id: 0,
  type: 'default',
  input: {
    type: AddOnInputType.INPUT,
    label: 'Payment Description',
    hint: 'Include an invoice number or description of service (max 140 characters).',
  },
};

@Component({
  selector: 'app-pay-without-invoice-form',
  templateUrl: './pay-without-invoice-form.component.html',
  styleUrls: ['./pay-without-invoice-form.component.scss'],
  standalone: false,
})
export class PayWithoutInvoiceFormComponent implements Validate, OnInit, OnDestroy {
  payWithoutInvoicePayable$ = new BehaviorSubject<Partial<PayWithoutInvoicePayable>>(undefined);
  @Input() set payWithoutInvoicePayable(payWithoutInvoicePayable: Partial<PayWithoutInvoicePayable>) {
    emitValueIfValid(payWithoutInvoicePayable, this.payWithoutInvoicePayable$, { allowNullish: true });
  }

  jobs$ = new BehaviorSubject<Loadable<BaseJob[]>>(undefined);
  @Input() set jobs(jobs: Loadable<BaseJob[]>) {
    emitValueIfValid(jobs, this.jobs$);
  }

  clients$ = new BehaviorSubject<Loadable<Client[]>>(undefined);
  @Input() set clients(clients: Loadable<Client[]>) {
    emitValueIfValid(clients, this.clients$);
  }

  paymentTypes$ = new BehaviorSubject<Loadable<AddOnOption[]>>(undefined);
  @Input() set paymentTypes(paymentTypes: Loadable<AddOnOption[]>) {
    emitValueIfValid(paymentTypes, this.paymentTypes$);
  }

  @Output() isValid = new EventEmitter<boolean>();
  @Output() payWithoutInvoicePayableChange = new EventEmitter<Partial<PayWithoutInvoicePayable>>();

  private readonly _unsubscribe$ = new Subject<void>();
  AddOnInputType = AddOnInputType;

  payWithoutInvoiceForm = new UntypedFormGroup({
    clientId: new UntypedFormControl(undefined, Validators.required),
    typeId: new UntypedFormControl(undefined, Validators.required),
    subtotal: new UntypedFormControl(0, [Validators.required, Validators.min(0.01), Validators.maxLength(20)]),
    notes: new UntypedFormControl(undefined, [Validators.required, Validators.maxLength(140)]),
  } as Record<keyof PayWithoutInvoicePayable, UntypedFormControl>);

  modifyFormOnNoPaymentTypes$ = getLoadedResults(this.paymentTypes$).pipe(
    take(1),
    filter(isEmpty),
    tap(_ => this.payWithoutInvoiceForm.removeControl('typeId')),
    takeUntil(this._unsubscribe$),
  );

  initializeFormValue$ = this.payWithoutInvoicePayable$.pipe(
    skip(1), // needed since the source for this input has a delay of one to workaround ExpressionChangedAfterItHasBeenCheckedError :(
    take(1),
    switchMap(payWithoutInvoicePayable => {
      if (payWithoutInvoicePayable == undefined) {
        return this.setPaymentTypeInitialSelection$;
      }

      return combineLatest([
        getLoadedResults(this.clients$),
        this.authAdaptor.isAdmin ? getLoadedResults(this.jobs$) : of(undefined),
      ]).pipe(
        tap(([clients, jobs]) => {
          // trim off clientId and jobId from the payWithoutInvoicePayable object
          const primitiveFormValues = Object.entries(payWithoutInvoicePayable).reduce(
            (primitiveFormValues, [key, value]) => {
              return (['clientId', 'jobId'] as (keyof PayWithoutInvoicePayable)[]).includes(key as keyof PayWithoutInvoicePayable)
                ? primitiveFormValues
                : { ...primitiveFormValues, [key]: value };
            },
            {} as Partial<Omit<PayWithoutInvoicePayable, 'clientId' | 'jobId'>>,
          );

          // set primitive form values
          this.payWithoutInvoiceForm.patchValue(primitiveFormValues);
          if (primitiveFormValues.subtotal == undefined) {
            this.payWithoutInvoiceForm.controls.subtotal.setValue(0);
          }

          // set client and job form values
          if (payWithoutInvoicePayable.clientId != undefined) {
            const matchingClient = clients.find(({ id }) => id === payWithoutInvoicePayable.clientId);
            this.payWithoutInvoiceForm.controls.clientId.setValue(matchingClient);
          }

          if (payWithoutInvoicePayable.jobId != undefined) {
            const matchingJob = jobs.find(({ id }) => id === payWithoutInvoicePayable.jobId);
            this.payWithoutInvoiceForm.controls.jobId.setValue(matchingJob);
          }
        }),
      );
    }),
    take(1),
    takeUntil(this._unsubscribe$),
  );

  fullyLoadedClients$ = combineLatest([
    getLoadedResults(this.clients$),
    this.store.select(CommonSettingsState.getClientCurrenciesStatus).pipe(
      isLoaded(true),
      switchMap(_ => this.store.selectOnce(CommonSettingsState.getClientCurrencies)),
    ),
  ]).pipe(
    map(([clients, clientCurrencies]) => clients.map(client => ({ ...client, currency: clientCurrencies[client.id]?.data }))),
  );

  resetFormOnPayWithoutInvoicePayableDeletion$ = this.payWithoutInvoicePayable$.pipe(
    ignoreTruthy(),
    filter(_ => this.payWithoutInvoiceForm.dirty),
    tap(_ => {
      // since typeId comes from a radio group and subtotal shouldn't ever be nullish, preserve some kind of value for them on reset
      this.payWithoutInvoiceForm.reset({ typeId: this.payWithoutInvoiceForm.value.typeId, subtotal: 0 });
      this.payWithoutInvoiceForm.markAsPristine();
    }),
    takeUntil(this._unsubscribe$),
  );

  clientDisplayFn = (client: Client & { currency: Currency }): string => `
    ${client.name}${client.currency.code !== 'USD' ? '&nbsp;– Non-USD' : ''}
  `;
  clientDisableFn = (client: Client & { currency: Currency }): boolean => client.currency.code !== 'USD';
  compareClientsFn = (client1: Client, client2: Client): boolean => client1.id === client2.id;
  sortClientsFn = (client1: Client, client2: Client): number => compareStringsIgnoreCase(client1.name, client2.name);

  jobDisplayFn = (job: BaseJob): string => job.name;
  compareJobsFn = (job1: BaseJob, job2: BaseJob): boolean => job1.id === job2.id;
  sortJobsFn = (job1: BaseJob, job2: BaseJob): number => compareStringsIgnoreCase(job1.name, job2.name);

  sortedPaymentTypes$: Observable<AddOnOption[]> = getLoadedResults(this.paymentTypes$).pipe(
    ignoreEmpty(),
    map(paymentTypes => {
      const miscellaneousPaymentType = paymentTypes.find(({ type }) => areStringsEqualIgnoreCase(type, 'MISCELLANEOUS'));

      return paymentTypes
        .filter(({ type }) => !areStringsEqualIgnoreCase(type, 'MISCELLANEOUS'))
        .sort((a, b) => compareStringsIgnoreCase(a.type, b.type))
        .concat(miscellaneousPaymentType ?? []);
    }),
    takeUntil(this._unsubscribe$),
  );

  setPaymentTypeInitialSelection$ = getLoadedResults(this.paymentTypes$).pipe(
    ignoreEmpty(),
    take(1),
    map(paymentTypes => {
      const miscellaneousPaymentType = paymentTypes.find(({ type }) => areStringsEqualIgnoreCase(type, 'MISCELLANEOUS'));
      if (miscellaneousPaymentType != undefined) {
        return miscellaneousPaymentType.id;
      }

      const alphabeticallyFirstPaymentType = paymentTypes.reduce(
        (first, paymentType) => compareStringsIgnoreCase(first.type, paymentType.type) < 0 ? first : paymentType,
        paymentTypes[0],
      );
      return alphabeticallyFirstPaymentType.id;
    }),
    tap(typeId => {
      this.payWithoutInvoiceForm.patchValue({ typeId });
      this.payWithoutInvoicePayableChange.emit({ typeId });
    }),
    takeUntil(this._unsubscribe$),
  );

  selectedPaymentType$ = combineLatest([
    getLoadedResults(this.paymentTypes$),
    getControlValueChanges<number>(this.payWithoutInvoiceForm.controls.typeId),
  ]).pipe(
    map(([paymentTypes, selectedPaymentTypeId]) => {
      if (isEmpty(paymentTypes)) {
        return DEFAULT_PAYMENT_TYPE;
      }

      return paymentTypes.find(({ id }) => id === selectedPaymentTypeId);
    }),
  );

  emitAllChanges$ = defer(() => merge(
    ...Object.entries(this.payWithoutInvoiceForm.controls).map(
      ([key, control]: [keyof PayWithoutInvoicePayable, UntypedFormControl]) => this._getFormControlChangeEmitter(key, control),
    ),
  ).pipe(
    takeUntil(this._unsubscribe$),
  ));

  emitIsValid$ = this.payWithoutInvoiceForm.statusChanges.pipe(
    map(status => status === 'VALID'),
    distinctUntilChanged(),
    tap(isValid => this.isValid.emit(isValid)),
    takeUntil(this._unsubscribe$),
  );

  constructor(
    readonly authAdaptor: AuthAdaptorService,
    private readonly store: Store,
  ) { }

  ngOnInit(): void {
    if (this.authAdaptor.isAdmin) {
      this.payWithoutInvoiceForm.addControl('jobId', new UntypedFormControl(undefined));
    }

    this.store.dispatch(new GetClientCurrencies(this.authAdaptor.user.getClientIdList()));
    this.initializeFormValue$.subscribe();
    this.resetFormOnPayWithoutInvoicePayableDeletion$.subscribe();
    this.emitAllChanges$.subscribe();
    this.emitIsValid$.subscribe();
    this.modifyFormOnNoPaymentTypes$.subscribe();
  }

  controlHasError(controlName: keyof PayWithoutInvoicePayable, error?: string): boolean {
    const control = this.payWithoutInvoiceForm.controls[controlName];
    if (control == undefined) {
      return false;
    }

    if (!error) {
      return control.errors != undefined && (control.touched || control.dirty);
    }

    return control.hasError(error) && (control.touched || control.dirty);
  }

  validate(): Observable<boolean> {
    this.payWithoutInvoiceForm.markAllAsTouched();
    return of(this.payWithoutInvoiceForm.valid);
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  private _getFormControlChangeEmitter(
    key: keyof PayWithoutInvoicePayable,
    control: UntypedFormControl,
  ): Observable<Partial<PayWithoutInvoicePayable>> {
    return getControlValueChanges(control, true).pipe(
      skip(1),
      ignoreNullish(),
      map<number | string | Client | BaseJob, Partial<PayWithoutInvoicePayable>>(value => {
        if (typeof value === 'number' || typeof value === 'string') {
          return { [key]: NON_NUMERIC_VALUES.includes(key) ? value : +value };
        }

        return { [key]: value.id };
      }),
      tap(partialPayWithoutInvoicePayable => this.payWithoutInvoicePayableChange.emit(partialPayWithoutInvoicePayable)),
    );
  }
}
